import React from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { isMap } from "util/types";

interface HowItWorksProps {
  onNext: () => void;
  onPrevious: () => void;
}

const HowItWorks: React.FC<HowItWorksProps> = ({ onNext, onPrevious }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={{
        minHeight: "100vh",
        background: "transparent",
        color: "white",
        py: 8,
        px: { xs: 2, md: 6 },
      }}
    >
      <Grid item xs={12} md={10}>
        {/* Title Section */}
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", mb: 10, textAlign: "center" }}
        >
          How It Works
        </Typography>

        {/* Steps Section */}
        <Grid container spacing={4} justifyContent="center" textAlign="center">
          {steps.map((step, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box
                sx={{
                  maxWidth: 600,
                  mx: "auto",
                  position: "relative",
                  overflow: "visible",
                  padding: 2,
                  mt: 4,
                }}
              >
                {/* SVG Background */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "-40%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "175px",
                    height: "175px",
                    backgroundImage: `url(${process.env.PUBLIC_URL}/howitworks${
                      index + 1
                    }.svg)`,
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    opacity: 1,
                    zIndex: 0,
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    mb: 1,
                    position: "relative",
                    zIndex: 1,
                    fontSize: "24px",
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "bold",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {step.subtitle}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mt: 1,
                    opacity: 0.8,
                    position: "relative",
                    zIndex: 1,
                    whiteSpace: "normal",
                    maxWidth: "100%",
                  }}
                >
                  {step.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Button Section */}
        <Box textAlign="center" mt={5}>
          <Box
            display="flex"
            flexDirection={{ xs: "column-reverse", sm: "row" }}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#7A7A7A",
                borderRadius: "30px",
                mr: { sm: 2 },
                mb: { xs: 1, sm: 0 },
                px: 4,
                py: 1.5,
                fontSize: "1rem",
                color: "white",
                textTransform: "none",
              }}
              onClick={onPrevious}
              fullWidth={isMobile ? true : false}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#007BFF",
                borderRadius: "30px",
                px: 4,
                py: 1.5,
                mb: { xs: 1, sm: 0 },
                fontSize: "1rem",
                color: "white",
                textTransform: "none",
                "&:hover": { backgroundColor: "#0056b3" },
              }}
              onClick={onNext}
              fullWidth={isMobile ? true : false}
            >
              Enter Playground
            </Button>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

const steps = [
  {
    title: "Step 1",
    subtitle: "Dive Right In",
    description: "Validate your info & enter the Eximius AI Playground",
  },
  {
    title: "Step 2",
    subtitle: "Hands-On Experience",
    description:
      "Discover capabilities such as dynamic job description creation, AI-driven candidate sourcing and matching.",
  },
  {
    title: "Step 3",
    subtitle: "See the Magic",
    description:
      "Watch how Eximius AI engages with candidates in real-time and what they experience.",
  },
];

export default HowItWorks;
